import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Layers, Ruler, Palette, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatPrice, LOCAL_TEMPLATES } from '@/lib/furnitureData';
import TemplateThumbnail3D from '@/components/TemplateThumbnail3D';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  wardrobe: 'Шкаф', table: 'Стол', shelf: 'Полка', bed: 'Кровать',
  dresser: 'Комод', bookcase: 'Стеллаж', nightstand: 'Тумба', kitchen_cabinet: 'Кухня',
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [serverTemplates, setServerTemplates] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    axios.get(`${API}/templates`)
      .then(r => setServerTemplates(Array.isArray(r.data) && r.data.length ? r.data : []))
      .catch(() => setServerTemplates([]));
  }, []);

  const templates = useMemo(() => {
    const list = (serverTemplates && serverTemplates.length) ? serverTemplates : LOCAL_TEMPLATES;
    if (filter === 'all') return list;
    return list.filter(t => t.category === filter);
  }, [serverTemplates, filter]);

  const categoriesWithCounts = useMemo(() => {
    const list = (serverTemplates && serverTemplates.length) ? serverTemplates : LOCAL_TEMPLATES;
    const counts = list.reduce((acc, t) => {
      acc[t.category] = (acc[t.category] || 0) + 1;
      return acc;
    }, {});
    return [{ id: 'all', label: `Все (${list.length})` },
      ...Object.entries(CATEGORY_LABELS)
        .filter(([k]) => counts[k])
        .map(([k, label]) => ({ id: k, label: `${label} (${counts[k]})` }))];
  }, [serverTemplates]);

  return (
    <div data-testid="landing-page">
      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/e0ef4c6d-bbaa-427f-a27a-e33bbc80a1cd/images/7cc1ad7ecaa555b469df96a0556c55eaf3282494f4941ff4502301bb40dcc644.png"
            alt="" className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 py-20 md:py-28">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-[#1C1C1C] leading-[1.1]"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Соберите мебель<br />
            <span className="text-[#C25934]">вашей мечты</span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-[#5C5C5C] max-w-lg leading-relaxed">
            Визуальный конструктор мебели с выбором материалов, фурнитуры и мгновенным расчётом стоимости.
            Экспортируйте чертежи в один клик.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              data-testid="hero-create-btn"
              onClick={() => navigate('/builder')}
              className="bg-[#C25934] hover:bg-[#A64A2B] text-white rounded-sm px-6 h-11 text-sm font-semibold"
            >
              Создать с нуля
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              data-testid="hero-templates-btn"
              variant="outline"
              onClick={() => document.getElementById('templates')?.scrollIntoView({ behavior: 'smooth' })}
              className="border-[#C25934] text-[#C25934] hover:bg-[#C25934]/5 rounded-sm px-6 h-11 text-sm font-semibold"
            >
              Шаблоны
            </Button>
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Layers, label: 'Модульная сборка' },
              { icon: Palette, label: '10+ материалов' },
              { icon: Ruler, label: 'Точные размеры' },
              { icon: Download, label: 'Экспорт чертежей' },
            ].map((feat, i) => (
              <div
                key={feat.label}
                className="flex items-center gap-2.5 p-3 rounded-sm bg-white/60 border border-black/5"
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <feat.icon className="w-4 h-4 text-[#C25934] shrink-0" />
                <span className="text-xs font-medium text-[#1C1C1C]">{feat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Templates */}
      <section id="templates" className="max-w-6xl mx-auto px-6 py-16" data-testid="templates-section">
        <h2
          className="text-2xl md:text-3xl font-bold tracking-tight text-[#1C1C1C] mb-2"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          Готовые шаблоны
        </h2>
        <p className="text-sm text-[#5C5C5C] mb-6">
          Выберите шаблон и настройте под себя —{' '}
          <span className="text-[#C25934] font-semibold">{LOCAL_TEMPLATES.length}+ готовых конструкций</span>
        </p>

        <div className="flex flex-wrap gap-2 mb-8" data-testid="template-filters">
          {categoriesWithCounts.map(c => (
            <button
              key={c.id}
              onClick={() => setFilter(c.id)}
              className={`px-3 h-8 rounded-sm border text-xs font-semibold transition-colors ${
                filter === c.id
                  ? 'bg-[#C25934] text-white border-[#C25934]'
                  : 'bg-white text-[#5C5C5C] border-[#E0DDD8] hover:border-[#C25934] hover:text-[#C25934]'
              }`}
              data-testid={`filter-${c.id}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((tpl, i) => (
            <div
              key={tpl.id}
              className="template-card bg-white rounded-sm border border-[#E0DDD8] overflow-hidden animate-fade-in-up"
              style={{ animationDelay: `${i * 80}ms` }}
              data-testid={`template-card-${tpl.id}`}
            >
              <div className="aspect-[4/3] overflow-hidden bg-gradient-to-b from-[#F5F2EC] to-[#E6E1D8] flex items-center justify-center transition-transform duration-500 hover:scale-[1.02]">
                <TemplateThumbnail3D template={tpl} />
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-[#1C1C1C]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    {tpl.name_ru}
                  </h3>
                  <Badge variant="secondary" className="bg-[#E8E5E1] text-[#5C5C5C] text-xs rounded-sm">
                    {CATEGORY_LABELS[tpl.category] || tpl.category}
                  </Badge>
                </div>
                <p className="text-xs text-[#5C5C5C] mb-3 line-clamp-2">{tpl.description_ru}</p>
                <div className="flex items-center justify-between">
                  <span className="mono-text text-sm font-semibold text-[#1C1C1C]">
                    {formatPrice(tpl.base_price)}
                  </span>
                  <Button
                    data-testid={`template-customize-${tpl.id}`}
                    onClick={() => navigate(`/builder?template=${tpl.id}`)}
                    size="sm"
                    className="bg-[#C25934] hover:bg-[#A64A2B] text-white rounded-sm text-xs h-8 px-4"
                  >
                    Настроить
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
